import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Single source of truth for machine statuses — matches DB enum
const MACHINE_STATUS_OPTIONS = [
  { value: 'operational', label: 'Opérationnelle',   className: 'bg-success/10 text-success',   icon: 'CheckCircle' },
  { value: 'maintenance', label: 'Maintenance',      className: 'bg-warning/10 text-warning',   icon: 'Wrench'      },
  { value: 'offline',     label: 'Hors ligne',       className: 'bg-error/10 text-error',       icon: 'XCircle'     },
];

const getStatusConfig = (status) =>
  MACHINE_STATUS_OPTIONS.find(o => o.value === status) || MACHINE_STATUS_OPTIONS[0];

const MachineInventoryTable = ({ machines, onViewHistory, onEdit, onStatusChange }) => {
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sortConfig?.field !== field) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const sortedMachines = [...machines]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.field];
    const bValue = b?.[sortConfig?.field];
    if (sortConfig?.direction === 'asc') return aValue > bValue ? 1 : -1;
    return aValue < bValue ? 1 : -1;
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Machine
                  <Icon name={getSortIcon('name')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-foreground">Modèle</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-foreground">Fournisseur</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Statut
                  <Icon name={getSortIcon('status')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('utilizationRate')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Utilisation
                  <Icon name={getSortIcon('utilizationRate')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('successRate')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors duration-200"
                >
                  Taux Réussite
                  <Icon name={getSortIcon('successRate')} size={16} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-foreground">Prochaine Maintenance</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedMachines?.map((machine) => {
              const maintenanceDue = new Date(machine?.nextMaintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
              
              return (
                <tr key={machine?.id} className="hover:bg-muted/30 transition-colors duration-150">
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-foreground">{machine?.name}</span>
                      <span className="text-xs text-muted-foreground">{machine?.serialNumber}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-foreground">{machine?.model}</span>
                      <span className="text-xs text-muted-foreground">{machine?.manufacturer}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-foreground">
                        {machine?.supplier?.companyName || machine?.supplier?.fullName || '-'}
                      </span>
                      {machine?.supplier?.email && (
                        <span className="text-xs text-muted-foreground">{machine?.supplier?.email}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <select
                      value={machine?.status}
                      onChange={(e) => onStatusChange?.(machine?.id, e.target.value)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${getStatusConfig(machine?.status)?.className}`}
                    >
                      {MACHINE_STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div
                          className={`rounded-full h-2 ${
                            machine?.utilizationRate >= 80 ? 'bg-success' :
                            machine?.utilizationRate >= 50 ? 'bg-warning': 'bg-muted-foreground'
                          }`}
                          style={{ width: `${machine?.utilizationRate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground">{machine?.utilizationRate}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-sm font-medium ${
                      machine?.successRate >= 95 ? 'text-success' :
                      machine?.successRate >= 90 ? 'text-warning': 'text-error'
                    }`}>
                      {machine?.successRate}%
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className={`text-sm ${
                        maintenanceDue ? 'text-warning font-medium' : 'text-foreground'
                      }`}>
                        {formatDate(machine?.nextMaintenance)}
                      </span>
                      {maintenanceDue && (
                        <span className="text-xs text-warning flex items-center gap-1 mt-1">
                          <Icon name="AlertCircle" size={12} />
                          Bientôt due
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewHistory(machine)}
                        iconName="History"
                      >
                        Historique
                      </Button>
                      {onEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(machine)}
                          iconName="Pencil"
                        >
                          Modifier
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MachineInventoryTable;